import { useLayoutEffect, useState } from 'preact/hooks';
import type { AuthMode, Credential } from '@/types';
import { establishSession, requestMagicLink } from '@/api/client';
import { emit as emitAccessChange } from '@/accessEvents';
import { acquirePageScrollLock, releasePageScrollLock } from '@/pageScrollLock';
import { EmailStep } from '@/components/EmailStep';
import { LoginStep } from '@/components/LoginStep';
import { SupportEmailProvider } from '@/supportContact';
import { logError } from '@/sdkConsole';
import { t, useT } from '@/i18n/t';

interface Props {
  domainId: number;
  // The domain's auth mode; ezAuth (or the default) reaches this surface. The SDK
  // routes BYO to the publisher's own goToLogin() and never mounts LoginFlow.
  authMode?: AuthMode;
  // The publisher's support email, so the shell's "contact the publisher" line
  // renders on the standalone login too (the checkout flows get it from their
  // own providers).
  supportEmail?: string;
  // When true (the default for a publisher-invoked login) the visitor can close
  // the surface; closing is just dismissal, never a checkout cancel.
  dismissible?: boolean;
  // Unmounts the flow (Back, dismiss, or a completed login).
  onClose: () => void;
}

type Step = 'login' | 'magiclink';

// LoginFlow is the standalone login surface behind ezsubscriptions.login() and
// [data-ezoic-login], for a returning subscriber who wants to sign in without
// first hitting a paywall. It reuses the checkout login bits — the
// passwordless-first EmailStep intent="login" (magic-link email primary,
// ezAuth password form opt-in, Continue with Google) and the magic-link
// send/confirmation via LoginStep — without any checkout/payment.
// A completed login establishes the content session and fires access:change so
// gated content unlocks in place.
export function LoginFlow({ domainId, authMode, supportEmail, dismissible = true, onClose }: Props) {
  useT();
  const [step, setStep] = useState<Step>('login');
  const [loading, setLoading] = useState(false);
  const [loginLinkSent, setLoginLinkSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The email the passwordless-first login screen collected, carried into the
  // magic-link step for prefill.
  const [magicLinkEmail, setMagicLinkEmail] = useState('');

  // Lock the host page's scroll while the login surface is on screen so the page
  // behind the fixed overlay can't scroll (see pageScrollLock).
  useLayoutEffect(() => {
    acquirePageScrollLock();
    return releasePageScrollLock;
  }, []);

  // An in-page login resolved a credential (ezAuth token). Establish the content
  // session and, on success, fire access:change so on-page gated UI re-renders;
  // then close. A failed establish (unknown visitor) or error still closes — the
  // visitor signed in, there is just nothing to unlock.
  const handleAuthenticated = async (credential: Credential) => {
    try {
      if (await establishSession(credential)) {
        emitAccessChange();
      }
    } catch (err) {
      logError('login establish session failed', err);
    }
    onClose();
  };

  // The login screen's primary action hands its typed email over: switch to the
  // magic-link step and fire the send immediately — the button said "email me a
  // sign-in link", so no second confirm click. A send failure lands on the
  // pre-filled form with the error inline. A bare handoff (the password form's
  // fallback link carries no email) just opens the form.
  const handleMagicLink = (email?: string) => {
    setError(null);
    setLoginLinkSent(false);
    setMagicLinkEmail(email ?? '');
    setStep('magiclink');
    if (email) {
      void handleLoginSubmit(email);
    }
  };

  const handleLoginSubmit = async (email: string) => {
    setLoading(true);
    setError(null);
    try {
      await requestMagicLink({ email, domainId, returnUrl: window.location.href });
      setLoginLinkSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loginFlow.sendLinkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = dismissible ? onClose : undefined;

  return (
    <SupportEmailProvider value={supportEmail}>
      {step === 'magiclink' ? (
        <LoginStep
          initialEmail={magicLinkEmail || undefined}
          onSubmit={handleLoginSubmit}
          onBack={() => setStep('login')}
          onDifferentEmail={() => {
            setError(null);
            setLoginLinkSent(false);
          }}
          loading={loading}
          sent={loginLinkSent}
          error={error}
          standalone
          onDismiss={handleDismiss}
        />
      ) : (
        <EmailStep
          intent="login"
          authMode={authMode}
          domainId={domainId}
          guestCheckoutEnabled={false}
          // No recordIdentityMethod: standalone login is outside the checkout
          // funnel, so it never records an identity_method_selected.
          // EmailStep requires onSubmit (the guest email path), but intent="login"
          // opens on the mounted login form with no guest option, so it never fires.
          onSubmit={() => {}}
          onAuthenticated={handleAuthenticated}
          onMagicLink={handleMagicLink}
          // The login form has nothing behind it, so it offers no Back — dismissal is
          // the close button only, gated on dismissible (a non-dismissible login then
          // has no escape, by design).
          onDismiss={handleDismiss}
          loading={false}
          error={null}
        />
      )}
    </SupportEmailProvider>
  );
}
